import { useEffect, useState } from 'react';

// components
import { InteractiveDescriptionDesktop } from './interactive-description-desktop';
import { InteractiveDescriptionMobile } from './interactive-description-mobile';

// utils
import { InteractiveDescriptionContextProvider } from './context';
import { useIsMobile, useIsTablet } from 'components/App/SizeProvider';
import { useSectionId } from 'utils/hooks/use-section-id';

import { useTracking as useTracking2 } from 'utils/tracking/track';

export interface InteractiveDescriptionProps {
  imageMap: ImageMap;
  contentHeadline: string;
  border: string;
  title: string;
  anchor: string;
  subtitle: string;
  infoText: string;
  contentIndex: number;
  titleInAnchor: boolean;
  isSectionReference: boolean;
  isChild: boolean;
}

export function trackInteractiveDescriptionInfoClick(
  trackClick: ReturnType<typeof useTracking2>['trackClick'],
) {
  return () =>
    trackClick({
      click_intent: 'more_info',
      click_element: 'interactive_description',
    });
}

export function InteractiveDescription({
  imageMap,
  contentHeadline,
  border,
  title,
  anchor,
  subtitle,
  infoText,
  contentIndex,
  titleInAnchor,
  isSectionReference,
  isChild,
}: Readonly<InteractiveDescriptionProps>) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);
  const [contentWhenClicked, setContentWhenClicked] = useState<Area['description'] | null>(null);
  const [clickedItem, setClickedItem] = useState<Area | null>(null);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    const preOpenedItem =
      imageMap.areas.find((area) => area.description?.preOpen) ?? imageMap.areas[0];

    if (preOpenedItem) {
      setClickedItem(preOpenedItem);
      setContentWhenClicked(preOpenedItem.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageMap.areas]);

  const handleCloseDescription = () => {
    setContentWhenClicked(null);
    setClickedItem(null);
  };

  return (
    <InteractiveDescriptionContextProvider
      imageMap={imageMap}
      clickedItem={clickedItem}
      setClickedItem={setClickedItem}
      contentWhenClicked={contentWhenClicked}
      setContentWhenClicked={setContentWhenClicked}
      isChild={isChild}
      infoText={infoText}
      contentHeadline={contentHeadline}
      isSectionReference={isSectionReference}
      handleCloseDescription={handleCloseDescription}
    >
      {isMobile || isTablet ? (
        <InteractiveDescriptionMobile
          border={border}
          title={title}
          infoText={infoText}
          sectionId={sectionId}
        />
      ) : (
        <InteractiveDescriptionDesktop
          border={border}
          title={title}
          subtitle={subtitle}
          isChild={isChild}
          sectionId={sectionId}
        />
      )}
    </InteractiveDescriptionContextProvider>
  );
}
